import { FC, memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import AppRouter from '@/components/AppRouter';
import StartupScreen from '@organisms/StartupScreen/StartupScreen';
import LayoutMain from '@organisms/layouts/LayoutMain';
import LayoutEmpty from '@organisms/layouts/LayoutEmpty';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useActions } from '@hooks/useActions';
import Navbar from '@molecules/Navbar/Navbar';
import { TITLE_PREFIX } from '@/configuration';
import Notifications from '@srm/ui/notifications';
import ModalMessages from '@srm/ui/modal-messages';
import { ConnectedVersionUpdateDialog } from '@srm/ui/shell/VersionUpdateDialog';
import AccountSwitchScreen from '@srm/ui/shell/AccountSwitch';
import { UnsavedEditScope } from '@srm/ui/lib/unsavedEdit';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { IDebugSettings } from '@models/IDebugSettings';
import { leavesPage } from '@/router/leavesPage';
import { versionUpdate } from '@utils/versionUpdate';
import TableCursorScrollProvider from '@providers/TableCursorScrollProvider/TableCursorScrollProvider';
import { matchPath, useLocation } from 'react-router';
import { publicRoutes } from '@/router';
import { RouteNames } from '@/router/router.types';
import { auth } from '@/auth';
import { isSupportSession } from '@/auth/supportSession';
import { TableColumnSettingsUserContext } from '@/context/TableColumnSettingsContext';

interface IAppLayoutProps {
    /** Публичный маршрут (/login, SAML, …) */
    isPublicRoute: boolean;
    /** Предпросмотр документа для поддержки */
    isDocumentSupportPreview: boolean;
}

const HEADER = <Navbar />;

const AppLayout = memo(({ isPublicRoute, isDocumentSupportPreview }: IAppLayoutProps) => {
    const { getCurrentUser, initializeSupportPreview } = useActions();

    useEffect(() => {
        if (isSupportSession()) return; // Пользователь уже загружен входным шлюзом.
        if (isDocumentSupportPreview) {
            // Preview намеренно изолирован от обычных API. startAuth уже восстановил access,
            // здесь только синхронизируем token-only сессию и снимаем общий loading.
            initializeSupportPreview();
            return;
        }
        // Access поверх режима: body → localStorage, cookie → память (после тихой ротации startAuth).
        // Пустая строка корректна: getCurrentUser в этом случае не дёргает /User, только SignInSettings.
        getCurrentUser(auth.getAccessToken() ?? '');
    }, [getCurrentUser, initializeSupportPreview, isDocumentSupportPreview]);
    const isLoading = useTypedSelector((state) => state.auth.isLoading);
    const [isStarting, setIsStarting] = useState(true);
    // Отладочный тумблер «без защиты правок» (жучок в шапке): ни вопроса при уходе, ни предупреждения браузера
    const [debugSettings] = useLocalStorageState<Partial<IDebugSettings>>('Debug-Settings', {});

    const isAuth = useTypedSelector((state) => state.auth.isAuth);
    const username = useTypedSelector((state) => state.auth.user?.username);

    // Layout выбираем по URL + isAuth: на public-роутах (/login, SAML, …)
    // всегда LayoutEmpty, даже если в Redux уже isAuth=true. Это убирает
    // мигание LoginForm внутри LayoutMain в промежутке между dispatch(login)
    // и navigate(returnUrl) в LoginForm.
    const useMainLayout = isAuth && !isPublicRoute && !isDocumentSupportPreview;

    // Пульс для «Сеансов» админки: кто сейчас у экрана. Только в рабочем макете —
    // не на странице входа и не в предпросмотре поддержки.
    useEffect(() => (useMainLayout ? auth.startPresence() : undefined), [useMainLayout]);

    const showStartup = isStarting || (!useMainLayout && isLoading);
    const finishStartup = useCallback(() => setIsStarting(false), []);

    useLayoutEffect(() => {
        if (!useMainLayout && isLoading) setIsStarting(true);
        else if (useMainLayout && !isLoading) setIsStarting(false);
    }, [isLoading, useMainLayout]);

    // На публичном маршруте держим тот же экран до commit страницы, включая lazy-код
    // и редирект с защищённого URL. Рабочий layout сохраняет свои скелетоны загрузки.
    const canRenderPage = !showStartup || (!isLoading && !useMainLayout);

    return (
        <>
            {showStartup && <StartupScreen />}
            <div style={{ display: showStartup ? 'none' : 'contents' }}>
                {canRenderPage && (
                    <TableColumnSettingsUserContext value={isAuth && !isDocumentSupportPreview ? username : undefined}>
                        <TableCursorScrollProvider username={username}>
                            <title>{`${TITLE_PREFIX}`}</title>
                            {/* несохранённая правка страницы (документ, карточки, настройки): уход по меню, ссылке,
                                «Назад», закрытие вкладки и смена учётной записи из меню профиля — с вопросом */}
                            <UnsavedEditScope
                                guardNavigation={!debugSettings.disableUnsavedChangesGuard}
                                leaves={leavesPage}
                            >
                                {useMainLayout ? (
                                    <LayoutMain header={HEADER}>
                                        <AppRouter onReady={finishStartup} hideLoadingFallback={showStartup} />
                                    </LayoutMain>
                                ) : (
                                    <LayoutEmpty>
                                        <AppRouter onReady={finishStartup} hideLoadingFallback={showStartup} />
                                    </LayoutEmpty>
                                )}
                                <AccountSwitchScreen />
                            </UnsavedEditScope>
                            <Notifications />
                            <ModalMessages />
                            <ConnectedVersionUpdateDialog source={versionUpdate} />
                        </TableCursorScrollProvider>
                    </TableColumnSettingsUserContext>
                )}
            </div>
        </>
    );
});

/**
 * Адрес корню нужен только для выбора макета: переход перерисовывает этот компонент, а оболочку (шапку, меню,
 * плашки, тосты) — только при смене макета.
 */
const App: FC = () => {
    const { pathname } = useLocation();
    return (
        <AppLayout
            isPublicRoute={publicRoutes.some((r) => matchPath(r.path, pathname) !== null)}
            isDocumentSupportPreview={matchPath(RouteNames.DOCUMENT_SUPPORT_PREVIEW, pathname) !== null}
        />
    );
};

export default App;
